import React, { useEffect, useMemo, useState } from "react";

const RIBBON_STORAGE_KEY = "interiorEditor.ribbonMode";

function getInitialRibbonMode() {
  try {
    const stored = localStorage.getItem(RIBBON_STORAGE_KEY);

    if (stored === "compact" || stored === "full" || stored === "auto") {
      return stored;
    }
  } catch {
    // ignore localStorage errors
  }

  return "auto";
}

function getShouldAutoCompact() {
  if (typeof window === "undefined") return false;

  return window.innerWidth < 1400;
}

function RibbonButton({
  active,
  children,
  onClick,
  disabled,
  danger,
  primary,
  compact,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "rounded-md border whitespace-nowrap transition disabled:opacity-40 disabled:cursor-not-allowed",
        compact ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
        active
          ? "bg-blue-600 text-white border-blue-600 shadow-sm"
          : primary
          ? "bg-slate-900 text-white border-slate-900 hover:bg-slate-800"
          : danger
          ? "bg-white text-red-600 border-red-200 hover:bg-red-50"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function RibbonGroup({ title, children, wide = false }) {
  return (
    <div
      className={[
        "rounded-lg border bg-white p-2 flex flex-col gap-2 shadow-sm",
        wide ? "min-w-[260px]" : "min-w-[190px]",
      ].join(" ")}
    >
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </div>

      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function SelectedInfo({ selectedLabel, compact }) {
  return (
    <div
      className={[
        "rounded-lg border bg-white shadow-sm",
        compact ? "px-3 py-1.5 min-w-[160px]" : "px-3 py-2 min-w-[220px]",
      ].join(" ")}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Selected
      </div>
      <div
        className={[
          "text-slate-700 truncate",
          compact ? "text-xs" : "text-sm",
        ].join(" ")}
      >
        {selectedLabel || "None"}
      </div>
    </div>
  );
}

function DropdownGroup({
  title,
  commands = [],
  open,
  onToggle,
  onClose,
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={[
          "px-3 py-1.5 rounded-md border text-sm bg-white text-slate-700",
          "hover:bg-slate-50 min-w-[96px] text-left flex items-center justify-between gap-2",
          open ? "border-blue-300 bg-blue-50" : "border-slate-200",
        ].join(" ")}
      >
        <span>{title}</span>
        <span className="text-[10px] text-slate-400">▾</span>
      </button>

      {open && (
        <div className="absolute left-0 top-9 z-50 w-56 rounded-lg border bg-white shadow-xl p-1">
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {title}
          </div>

          {commands.map((cmd) => (
            <button
              key={cmd.key}
              type="button"
              disabled={cmd.disabled}
              onClick={() => {
                cmd.onClick?.();
                onClose?.();
              }}
              className={[
                "w-full text-left px-3 py-2 rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed",
                cmd.active
                  ? "bg-blue-600 text-white"
                  : cmd.danger
                  ? "text-red-600 hover:bg-red-50"
                  : "text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              <div className="flex items-center justify-between gap-2">
                <span>{cmd.label}</span>
                {cmd.active && <span className="text-xs">✓</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function deriveWorkflowMode({ workspaceMode, toolMode, viewMode, isolateMode }) {
  if (workspaceMode && workspaceMode !== "custom") {
    return workspaceMode;
  }

  if (toolMode === "select" && viewMode === "full" && !isolateMode) {
    return "design";
  }

  if (toolMode === "select" && viewMode === "xray" && isolateMode) {
    return "inspect";
  }

  if (toolMode === "partition" && viewMode === "box" && isolateMode) {
    return "grid";
  }

  if (toolMode === "select" && viewMode === "realistic" && !isolateMode) {
    return "present";
  }

  return "custom";
}

export default function EditorRibbon({
  workspaceMode,
  onWorkspaceModeChange,

  toolMode,
  setToolMode,

  viewMode,
  setViewMode,

  isolateMode,
  setIsolateMode,
  canIsolate,

  selectedLabel,

  onOpenLibrary,
  onSaveTemplate,
  onAddChild,
  onRemove,

  canSaveTemplate,
  canRemove,

  sceneViewSettings,
  onSceneViewSettingsChange,
}) {
  const [ribbonMode, setRibbonMode] = useState(getInitialRibbonMode);
const [autoCompact, setAutoCompact] = useState(getShouldAutoCompact);
  const [openMenu, setOpenMenu] = useState(null);
  const compactMode =
  ribbonMode === "compact" ||
  (ribbonMode === "auto" && autoCompact);

  const activeWorkflow = deriveWorkflowMode({
    workspaceMode,
    toolMode,
    viewMode,
    isolateMode,
  });

  function setCustomMode() {
    onWorkspaceModeChange?.("custom");
  }

  function patchSceneViewSettings(patch) {
  onSceneViewSettingsChange?.((prev) => ({
    ...(prev || {}),
    ...(patch || {}),
  }));
}

function requestStandardView(view) {
  patchSceneViewSettings({
    requestedView: {
      view,
      ts: Date.now(),
    },
  });
}

  function applyWorkflow(mode) {
  if (onWorkspaceModeChange) {
    onWorkspaceModeChange(mode);
    return;
  }

  if (mode === "design") {
    setToolMode?.("select");
    setViewMode?.("full");
    setIsolateMode?.(false);
    return;
  }

  if (mode === "inspect") {
    setToolMode?.("select");
    setViewMode?.("xray");
    setIsolateMode?.(true);
    return;
  }

  if (mode === "grid") {
    setToolMode?.("partition");
    setViewMode?.("box");
    setIsolateMode?.(true);
    return;
  }

  if (mode === "present") {
    setToolMode?.("select");
    setViewMode?.("realistic");
    setIsolateMode?.(false);
    return;
  }

  if (mode === "documentation") {
    setToolMode?.("select");
    setViewMode?.("hiddenLine");
    setIsolateMode?.(false);
  }
}

  const workflowCommands = useMemo(
  () => [
    {
      key: "design",
      label: "Design",
      active: activeWorkflow === "design",
      onClick: () => applyWorkflow("design"),
    },
    {
      key: "inspect",
      label: "Inspect",
      active: activeWorkflow === "inspect",
      disabled: !canIsolate,
      onClick: () => applyWorkflow("inspect"),
    },
    {
      key: "grid",
      label: "Grid Edit",
      active: activeWorkflow === "grid",
      disabled: !canIsolate,
      onClick: () => applyWorkflow("grid"),
    },
    {
      key: "present",
      label: "Present",
      active: activeWorkflow === "present",
      onClick: () => applyWorkflow("present"),
    },
    {
      key: "documentation",
      label: "Documentation",
      active: activeWorkflow === "documentation",
      onClick: () => applyWorkflow("documentation"),
    },
  ],
  [activeWorkflow, canIsolate, onWorkspaceModeChange]
);

  const toolCommands = useMemo(
    () => [
      {
        key: "select",
        label: "Select",
        active: toolMode === "select",
        onClick: () => {
          setCustomMode();
          setToolMode?.("select");
        },
      },
      {
        key: "move",
        label: "Move",
        active: toolMode === "move",
        onClick: () => {
          setCustomMode();
          setToolMode?.("move");
        },
      },
      {
        key: "rotate",
        label: "Rotate",
        active: toolMode === "rotate",
        onClick: () => {
          setCustomMode();
          setToolMode?.("rotate");
        },
      },
    ],
    [toolMode]
  );

  const modifyCommands = useMemo(
    () => [
      {
        key: "align",
        label: "Align",
        active: toolMode === "align",
        onClick: () => {
          setCustomMode();
          setToolMode?.("align");
        },
      },
      {
        key: "match",
        label: "Match",
        active: toolMode === "match",
        onClick: () => {
          setCustomMode();
          setToolMode?.("match");
        },
      },
      {
        key: "cut",
        label: "Cut/Grid",
        active: toolMode === "partition",
        disabled: !canIsolate,
        onClick: () => applyWorkflow("grid"),
      },
      {
        key: "isolate",
        label: isolateMode ? "Show all" : "Isolate",
        active: isolateMode,
        disabled: !canIsolate,
        onClick: () => {
          setCustomMode();
          setIsolateMode?.(!isolateMode);
        },
      },
    ],
    [toolMode, isolateMode, canIsolate]
  );

  const createCommands = useMemo(
    () => [
      {
        key: "add",
        label: "Add cabinet",
        onClick: onOpenLibrary,
      },
      {
        key: "template",
        label: "Save template",
        disabled: !canSaveTemplate,
        onClick: onSaveTemplate,
      },
      {
        key: "child",
        label: "Add child",
        onClick: onAddChild,
      },
      {
        key: "remove",
        label: "Remove",
        disabled: !canRemove,
        danger: true,
        onClick: onRemove,
      },
    ],
    [
      onOpenLibrary,
      onSaveTemplate,
      onAddChild,
      onRemove,
      canSaveTemplate,
      canRemove,
    ]
  );

  const viewCommands = useMemo(
    () => [
      {
        key: "box",
        label: "Box",
        active: viewMode === "box",
        onClick: () => {
          setCustomMode();
          setViewMode?.("box");
        },
      },
      {
        key: "boards",
        label: "Boards",
        active: viewMode === "boards",
        onClick: () => {
          setCustomMode();
          setViewMode?.("boards");
        },
      },
      {
        key: "full",
        label: "Full",
        active: viewMode === "full",
        onClick: () => {
          setCustomMode();
          setViewMode?.("full");
        },
      },
      {
        key: "realistic",
        label: "Realistic",
        active: viewMode === "realistic",
        onClick: () => {
          setCustomMode();
          setViewMode?.("realistic");
        },
      },
      {
        key: "xray",
        label: "X-Ray",
        active: viewMode === "xray",
        onClick: () => {
          setCustomMode();
          setViewMode?.("xray");
        },
      },
      {
  key: "wireframe",
  label: "Wireframe",
  active: viewMode === "wireframe",
  onClick: () => {
    setCustomMode();
    setViewMode?.("wireframe");
  },
},
{
  key: "hiddenLine",
  label: "Hidden Line",
  active: viewMode === "hiddenLine",
  onClick: () => {
    setCustomMode();
    setViewMode?.("hiddenLine");
  },
},
    ],
    [viewMode]
  );

  const sceneCommands = useMemo(
  () => [
    {
      key: "perspective",
      label: "Perspective",
      active: sceneViewSettings?.cameraType !== "orthographic",
      onClick: () => patchSceneViewSettings({ cameraType: "perspective" }),
    },
    {
      key: "orthographic",
      label: "Orthographic",
      active: sceneViewSettings?.cameraType === "orthographic",
      onClick: () => patchSceneViewSettings({ cameraType: "orthographic" }),
    },
    {
      key: "iso",
      label: "ISO",
      onClick: () => requestStandardView("iso"),
    },
    {
      key: "front",
      label: "Front",
      onClick: () => requestStandardView("front"),
    },
    {
      key: "back",
      label: "Back",
      onClick: () => requestStandardView("back"),
    },
    {
      key: "left",
      label: "Left",
      onClick: () => requestStandardView("left"),
    },
    {
      key: "right",
      label: "Right",
      onClick: () => requestStandardView("right"),
    },
    {
      key: "top",
      label: "Top",
      onClick: () => requestStandardView("top"),
    },
    {
  key: "fitSelected",
  label: "Fit Selected",
  onClick: () =>
    patchSceneViewSettings({
      requestedAction: {
        type: "fitSelected",
        ts: Date.now(),
      },
    }),
},
{
  key: "fitAll",
  label: "Fit All",
  onClick: () =>
    patchSceneViewSettings({
      requestedAction: {
        type: "fitAll",
        ts: Date.now(),
      },
    }),
},
{
  key: "resetView",
  label: "Reset View",
  onClick: () =>
    patchSceneViewSettings({
      requestedAction: {
        type: "resetView",
        ts: Date.now(),
      },
    }),
},
{
  key: "workLight",
  label: "Light: Work",
  onClick: () =>
    patchSceneViewSettings({
      ambientIntensity: 0.9,
      mainLightIntensity: 1,
    }),
},
{
  key: "presentLight",
  label: "Light: Present",
  onClick: () =>
    patchSceneViewSettings({
      ambientIntensity: 1.15,
      mainLightIntensity: 1.45,
    }),
},
{
  key: "technicalLight",
  label: "Light: Technical",
  onClick: () =>
    patchSceneViewSettings({
      ambientIntensity: 0.65,
      mainLightIntensity: 0.85,
    }),
},
    {
      key: "grid",
      label: sceneViewSettings?.showGrid === false ? "Grid: Off" : "Grid: On",
      active: sceneViewSettings?.showGrid !== false,
      onClick: () =>
        patchSceneViewSettings({
          showGrid: !(sceneViewSettings?.showGrid !== false),
        }),
    },
    {
      key: "gizmo",
      label: sceneViewSettings?.showGizmo === false ? "Gizmo: Off" : "Gizmo: On",
      active: sceneViewSettings?.showGizmo !== false,
      onClick: () =>
        patchSceneViewSettings({
          showGizmo: !(sceneViewSettings?.showGizmo !== false),
        }),
    },
    {
      key: "axes",
      label:
  sceneViewSettings?.showCoordinateSystems === true
    ? "Axes: On"
    : "Axes: Off",
      active: sceneViewSettings?.showCoordinateSystems === true,
      onClick: () =>
        patchSceneViewSettings({
          showCoordinateSystems: !sceneViewSettings?.showCoordinateSystems,
        }),
    },
  ],
  [sceneViewSettings]
);

  const allMenus = {
  tools: toolCommands,
  modify: modifyCommands,
  create: createCommands,
  view: viewCommands,
  scene: sceneCommands,
};

  function RibbonModeSwitch({ ribbonMode, onChange }) {
  const modes = [
    ["auto", "Auto"],
    ["compact", "Compact"],
    ["full", "Full"],
  ];

  return (
    <div className="rounded-lg border bg-white p-1 flex items-center gap-1 shadow-sm">
      {modes.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange?.(key)}
          className={[
            "px-2.5 py-1 rounded text-xs transition",
            ribbonMode === key
              ? "bg-blue-600 text-white"
              : "text-slate-600 hover:bg-slate-50",
          ].join(" ")}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

  useEffect(() => {
  try {
    localStorage.setItem(RIBBON_STORAGE_KEY, ribbonMode);
  } catch {
    // ignore localStorage errors
  }
}, [ribbonMode]);

useEffect(() => {
  function handleResize() {
    setAutoCompact(getShouldAutoCompact());
  }

  handleResize();

  window.addEventListener("resize", handleResize);

  return () => {
    window.removeEventListener("resize", handleResize);
  };
}, []);

  return (
    <div className="shrink-0 border-b bg-slate-50 px-3 py-2">
      {compactMode ? (
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-lg border bg-white p-1 flex flex-wrap gap-1 shadow-sm">
            {workflowCommands.map((cmd) => (
              <RibbonButton
                key={cmd.key}
                compact
                active={cmd.active}
                disabled={cmd.disabled}
                onClick={cmd.onClick}
              >
                {cmd.label}
              </RibbonButton>
            ))}
          </div>

          <DropdownGroup
            title="Tools"
            commands={allMenus.tools}
            open={openMenu === "tools"}
            onToggle={() =>
              setOpenMenu((prev) => (prev === "tools" ? null : "tools"))
            }
            onClose={() => setOpenMenu(null)}
          />

          <DropdownGroup
            title="Modify"
            commands={allMenus.modify}
            open={openMenu === "modify"}
            onToggle={() =>
              setOpenMenu((prev) => (prev === "modify" ? null : "modify"))
            }
            onClose={() => setOpenMenu(null)}
          />

          <DropdownGroup
            title="Create"
            commands={allMenus.create}
            open={openMenu === "create"}
            onToggle={() =>
              setOpenMenu((prev) => (prev === "create" ? null : "create"))
            }
            onClose={() => setOpenMenu(null)}
          />

          <DropdownGroup
            title="View"
            commands={allMenus.view}
            open={openMenu === "view"}
            onToggle={() =>
              setOpenMenu((prev) => (prev === "view" ? null : "view"))
            }
            onClose={() => setOpenMenu(null)}
          />

          <DropdownGroup
  title="Scene"
  commands={allMenus.scene}
  open={openMenu === "scene"}
  onToggle={() =>
    setOpenMenu((prev) => (prev === "scene" ? null : "scene"))
  }
  onClose={() => setOpenMenu(null)}
/>

          <SelectedInfo selectedLabel={selectedLabel} compact />

          <div className="ml-auto">
  <RibbonModeSwitch
    ribbonMode={ribbonMode}
    onChange={(mode) => {
      setOpenMenu(null);
      setRibbonMode(mode);
    }}
  />
</div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 items-start">
          <RibbonGroup title="Workflow">
            {workflowCommands.map((cmd) => (
              <RibbonButton
                key={cmd.key}
                active={cmd.active}
                disabled={cmd.disabled}
                onClick={cmd.onClick}
              >
                {cmd.label}
              </RibbonButton>
            ))}
          </RibbonGroup>

          <RibbonGroup title="Tools">
            {toolCommands.map((cmd) => (
              <RibbonButton
                key={cmd.key}
                active={cmd.active}
                onClick={cmd.onClick}
              >
                {cmd.label}
              </RibbonButton>
            ))}
          </RibbonGroup>

          <RibbonGroup title="Modify" wide>
            {modifyCommands.map((cmd) => (
              <RibbonButton
                key={cmd.key}
                active={cmd.active}
                disabled={cmd.disabled}
                onClick={cmd.onClick}
              >
                {cmd.label}
              </RibbonButton>
            ))}
          </RibbonGroup>

          <RibbonGroup title="Create" wide>
            {createCommands.map((cmd) => (
              <RibbonButton
                key={cmd.key}
                danger={cmd.danger}
                disabled={cmd.disabled}
                onClick={cmd.onClick}
              >
                {cmd.label}
              </RibbonButton>
            ))}
          </RibbonGroup>

          <RibbonGroup title="View" wide>
            {viewCommands.map((cmd) => (
              <RibbonButton
                key={cmd.key}
                active={cmd.active}
                onClick={cmd.onClick}
              >
                {cmd.label}
              </RibbonButton>
            ))}
          </RibbonGroup>

          <RibbonGroup title="Scene" wide>
  {sceneCommands.map((cmd) => (
    <RibbonButton
      key={cmd.key}
      active={cmd.active}
      disabled={cmd.disabled}
      onClick={cmd.onClick}
    >
      {cmd.label}
    </RibbonButton>
  ))}
</RibbonGroup>

          <SelectedInfo selectedLabel={selectedLabel} />

          <div className="ml-auto">
  <RibbonModeSwitch
    ribbonMode={ribbonMode}
    onChange={setRibbonMode}
  />
</div>
        </div>
      )}
    </div>
  );
}